import { Component, OnInit, OnDestroy } from '@angular/core';
import { WompiWebhookService } from '../../services/wompi-webhook.service';
import {
  WompiWebhookRequest,
  WompiWebhookRequestFilter,
  WompiWebhookStats,
  WompiRequestStatus,
  WompiPagedResult
} from '../../interfaces/wompi-webhook.interfaces';
import { ToastService } from '../../services/toast.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-wompi-requests',
  templateUrl: './wompi-requests.component.html',
  styleUrls: ['./wompi-requests.component.scss']
})
export class WompiRequestsComponent implements OnInit, OnDestroy {
  requests: WompiWebhookRequest[] = [];
  filteredRequests: WompiWebhookRequest[] = [];
  stats: WompiWebhookStats | null = null;
  loading = false;

  // Filtros
  filter: WompiWebhookRequestFilter = {};
  statusFilter: WompiRequestStatus | null = null;
  searchText = '';

  // Paginación
  pageNumber = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 0;
  hasPreviousPage = false;
  hasNextPage = false;
  pageSizeOptions = [10, 20, 50, 100];

  // Subject para debounce de búsqueda
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Enum para el template
  WompiRequestStatus = WompiRequestStatus;

  constructor(
    private wompiService: WompiWebhookService,
    private toastService: ToastService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Configurar debounce para búsqueda
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.searchText = searchTerm;
      this.pageNumber = 1; // Reset a primera página
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): void {
    this.loading = true;

    // Construir filtro con paginación y búsqueda
    const pagedFilter: WompiWebhookRequestFilter = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchTerm: this.searchText || undefined,
      status: this.statusFilter !== null ? this.statusFilter : undefined,
      sortDirection: 'desc'
    };

    // Cargar solicitudes paginadas
    this.wompiService.getRequestsPaged(pagedFilter).subscribe({
      next: (result: WompiPagedResult) => {
        this.filteredRequests = result.data;
        this.totalCount = result.totalCount;
        this.totalPages = result.totalPages;
        this.hasPreviousPage = result.hasPreviousPage;
        this.hasNextPage = result.hasNextPage;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error cargando solicitudes:', error);
        this.toastService.showError('Error cargando solicitudes de Wompi');
        this.loading = false;
      }
    });

    // Cargar estadísticas (solo una vez al inicio)
    if (!this.stats) {
      this.wompiService.getStats().subscribe({
        next: (stats) => {
          this.stats = stats;
        },
        error: (error) => {
          console.error('Error cargando estadísticas:', error);
        }
      });
    }
  }

  onStatusFilterChange(): void {
    this.pageNumber = 1; // Reset a primera página
    this.loadData();
  }

  onSearchChange(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.searchSubject.next(searchTerm);
  }

  clearFilters(): void {
    this.statusFilter = null;
    this.searchText = '';
    this.pageNumber = 1;
    this.loadData();
  }

  // Métodos de paginación
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.pageNumber = page;
      this.loadData();
    }
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageNumber = 1; // Reset a primera página
    this.loadData();
  }

  goToFirstPage(): void {
    this.onPageChange(1);
  }

  goToLastPage(): void {
    this.onPageChange(this.totalPages);
  }

  goToPreviousPage(): void {
    this.onPageChange(this.pageNumber - 1);
  }

  goToNextPage(): void {
    this.onPageChange(this.pageNumber + 1);
  }

  get displayRangeStart(): number {
    return (this.pageNumber - 1) * this.pageSize + 1;
  }

  get displayRangeEnd(): number {
    return Math.min(this.pageNumber * this.pageSize, this.totalCount);
  }

  viewDetail(request: WompiWebhookRequest): void {
    this.router.navigate(['/wompi-requests', request.id]);
  }

  retryRequest(request: WompiWebhookRequest, event: Event): void {
    event.stopPropagation();

    if (request.status === WompiRequestStatus.Success) {
      this.toastService.showWarning('Esta solicitud ya fue procesada exitosamente');
      return;
    }

    if (request.status === WompiRequestStatus.Processing) {
      this.toastService.showWarning('Esta solicitud está siendo procesada actualmente');
      return;
    }

    if (confirm(`¿Está seguro de reprocesar la solicitud de transacción: ${request.transactionId}?`)) {
      this.wompiService.retryProcessing(request.id).subscribe({
        next: (response) => {
          this.toastService.showSuccess(response.message);
          this.loadData();
        },
        error: (error) => {
          console.error('Error reprocesando solicitud:', error);
          this.toastService.showError(error.error || 'Error al reprocesar la solicitud');
        }
      });
    }
  }

  getStatusName(status: WompiRequestStatus): string {
    return this.wompiService.getStatusName(status);
  }

  getStatusBadgeClass(status: WompiRequestStatus): string {
    return this.wompiService.getStatusBadgeClass(status);
  }

  getStatusIcon(status: WompiRequestStatus): string {
    return this.wompiService.getStatusIcon(status);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES');
  }

  formatAmount(amountInCents: number, currency?: string): string {
    return this.wompiService.formatAmount(amountInCents, currency || 'COP');
  }

  formatProcessingTime(timeMs: number | null | undefined): string {
    if (!timeMs) return '-';

    if (timeMs < 1000) {
      return `${timeMs}ms`;
    } else if (timeMs < 60000) {
      return `${(timeMs / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(timeMs / 60000);
      const seconds = Math.floor((timeMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  }

  getTransactionStatusColor(status: string): string {
    return this.wompiService.getTransactionStatusColor(status);
  }

  getPaymentMethodIcon(paymentMethodType: string): string {
    return this.wompiService.getPaymentMethodIcon(paymentMethodType);
  }

  getCommonErrorsArray(): { step: string, count: number }[] {
    if (!this.stats || !this.stats.commonErrors) return [];
    return Object.entries(this.stats.commonErrors)
      .map(([step, count]) => ({ step, count }))
      .sort((a, b) => b.count - a.count);
  }

  getTopTransactionStatusesArray(): { status: string, count: number }[] {
    if (!this.stats || !this.stats.topTransactionStatuses) return [];
    return Object.entries(this.stats.topTransactionStatuses)
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);
  }

  getTopPaymentMethodsArray(): { method: string, count: number }[] {
    if (!this.stats || !this.stats.topPaymentMethods) return [];
    return Object.entries(this.stats.topPaymentMethods)
      .map(([method, count]) => ({ method, count }))
      .sort((a, b) => b.count - a.count);
  }

  getEnvironmentDistributionArray(): { environment: string, count: number }[] {
    if (!this.stats || !this.stats.environmentDistribution) return [];
    return Object.entries(this.stats.environmentDistribution)
      .map(([environment, count]) => ({ environment, count }))
      .sort((a, b) => b.count - a.count);
  }
}
